// ============================================================
// NETCOM SIM API CLIENT — KORE ConnectivityPro shape (V4 REST)
// ============================================================
// Wraps the /sims/* routes on our REST API. The Lambda layer
// translates UI-friendly paths into the real KORE ConnectivityPro
// REST surface:
//
//   GET  /v1/accounts/{aid}/subscriptions                 — list SIMs
//   GET  /v1/accounts/{aid}/subscriptions/{sid}           — get SIM
//   GET  /v1/accounts/{aid}/subscriptions?iccid={iccid}   — lookup by ICCID
//   POST /v1/accounts/{aid}/provisioning-requests         — any state change
//   GET  /v1/accounts/{aid}/provisioning-requests/{rid}   — poll status
//   GET  /v1/accounts/{aid}/subscriptions/{sid}/usage-records
//   GET  /v1/accounts/{aid}/plans
//   GET  /v1/accounts/{aid}/features
//   GET  /v1/accounts/{aid}/cost-centers
//   GET  /v1/accounts/{aid}/activation-profiles
//   GET  /v1/ping
//
// IMPORTANT (2026-05-29 research): OmniSIM Reach uses ConnectivityPro,
// NOT Super SIM. They are two different KORE product stacks with two
// different APIs. The Super SIM API doc looks the most polished but
// it's the WRONG docs for us. Wire to ConnectivityPro at integration
// time — see `docs/KORE-API-SETUP.md`.
//
// Two modes:
//   1. Mock mode (VITE_USE_MOCK_API !== 'false') — local fixtures,
//      synthesizes a fake subscription_id from the ICCID so the
//      shape mirrors the live response.
//   2. Live mode — calls VITE_API_BASE/sims, sends Cognito ID token
//      in Authorization header. Lambda holds the KORE API client +
//      Netcom's account-id (in Secrets Manager: kore/account_id).
//
// Provisioning-request pattern: every lifecycle change is async.
// `changeSimState()` returns a `requestId` immediately; UI must
// poll via `getProvisioningStatus(requestId)` OR subscribe to a
// websocket event when the webhook arrives. See `sim-provisioning.js`
// for the polling/subscription wrapper.

// Mock fixture is a .js module (not JSON) so lastSeenAt timestamps
// are fresh every page load — keeps the demo's "Last seen 5m ago"
// labels honest instead of going stale at build time.
import simsMock from '../data/mock/sims.js';
import { getSession } from './auth.js';
import { sanitizePricing } from './compliance.js';
import { isDemoAccount } from '../config/demo.js';

const USE_MOCK = import.meta.env.VITE_USE_MOCK_API !== 'false';
const API_BASE = import.meta.env.VITE_API_BASE || '/api';
const MOCK_LATENCY_MS = 200;

// Demo-customer override — same pattern as starlink-api.js. Routes
// every SIM call through the mock branch when a netcom-admin started
// a demo session via Sidebar "Open Demo Customer".
function inDemoSession() {
  try {
    return typeof sessionStorage !== 'undefined'
      && !!sessionStorage.getItem('netcom_connect_demo_customer_session');
  } catch { return false; }
}
// The anonymized live-demo (Harbor Point Security, DEMO-HPS-001) signs in with a
// REAL Cognito JWT, so it isn't caught by inDemoSession() above. Its Starlink
// data is real Radius telemetry (anonymized server-side), but the backend has
// no Netcom Connect SIMs for it — so we serve the flagship multi-carrier SIM
// fleet client-side from the mock fixture. Scoped strictly to the demo account;
// real customers always hit the live KORE-backed API.
function isLiveDemoAccount() {
  try { return isDemoAccount(getSession()?.accountNumber); } catch { return false; }
}

/**
 * SIM product access for the signed-in session:
 *   'live' — customer has the 'sim' product → real (or demo) SIM data
 *   'none' — customer does not have the 'sim' product → no SIM section
 *
 * REMOVED (2026-08-06): a 'preview' state used to show Starlink-only
 * customers a labelled sample SIM showcase as a cross-sell. Pulled after
 * it read as confusing/wrong on a real customer's live dashboard — a
 * "PREVIEW" badge next to fabricated numbers (e.g. "24 SIMs", failover
 * counts) sitting right beside their real Starlink stats was too easy to
 * misread as their own fleet. If cross-sell for the SIM product comes
 * back, it should be a deliberate marketing surface, not embedded in the
 * operational dashboard.
 */
export function getSimAccess() {
  try {
    const p = getSession()?.products || [];
    if (p.includes('sim')) return 'live';
  } catch { /* fall through */ }
  return 'none';
}
export function isSimPreview() { return false; }

// Preview customers are served the sample fleet client-side (never the live
// KORE API — which would 403 for an account with no SIM product).
function isMockMode() { return USE_MOCK || inDemoSession() || isLiveDemoAccount() || isSimPreview(); }

// True when the SIM data on screen is synthetic (demo/mock/preview) rather than
// a real KORE-backed fleet. Gates sample-only affordances (failover simulator,
// pooled-data card, heatmap, fleet map). For a real 'live' SIM customer this is
// false, so they only ever see their real data.
export function isSimulatedData() { return isMockMode(); }
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

function authHeader() {
  // Same Cognito gotcha as starlink-api.js — API Gateway's
  // CognitoUserPoolsAuthorizer validates ID tokens, not access tokens.
  // Send idToken; fall back to accessToken only for legacy compatibility.
  const session = getSession();
  const token = session?.idToken || session?.accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function liveFetch(path, init = {}) {
  const res = await fetch(`${API_BASE.replace(/\/$/, '')}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(),
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    const err = new Error(`SIM API ${init.method || 'GET'} ${path} failed: ${res.status} ${body.slice(0, 200)}`);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

// ─── Public API ──────────────────────────────────────────────

// In ConnectivityPro every SIM has BOTH:
//   - iccid          — physical identifier, human-readable, what we show
//   - subscription_id — KORE's primary key, what every state-change uses
// Mock mode derives subscription_id deterministically so the UI can pre-cache
// the mapping just like a real response would carry both.
function deriveSubscriptionId(iccid) {
  return iccid ? `SUB-${iccid.slice(-10)}` : null;
}

function ensureBothIds(sim) {
  if (!sim) return null;
  return {
    ...sim,
    subscriptionId: sim.subscriptionId || deriveSubscriptionId(sim.iccid),
  };
}

export async function listSims({ cursor } = {}) {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    return { items: simsMock.map(ensureBothIds), nextCursor: null };
  }
  // Lambda translates to:
  //   GET /v1/accounts/{aid}/subscriptions?cursor=...
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  const data = await liveFetch(`/sims${qs}`);
  // Defense-in-depth: even though the Lambda is supposed to scrub
  // wholesale fields, run sanitizePricing() here too. If a future Lambda
  // change forgets to strip a field, the bundled compliance.js stops it.
  return {
    ...data,
    items: (data.items || []).map(sanitizePricing),
  };
}

export async function getSim(iccid) {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    return ensureBothIds(simsMock.find((s) => s.iccid === iccid)) || null;
  }
  // Lambda translates to:
  //   1. GET /v1/accounts/{aid}/subscriptions?iccid={iccid} → resolve subscription_id
  //   2. GET /v1/accounts/{aid}/subscriptions/{subscription_id} → full record
  // Caches the iccid → subscription_id mapping in RDS to skip step 1 on hot
  // paths. Mapping table lives in `infra/lib/migrations/0042-sim-mapping.sql`.
  const data = await liveFetch(`/sims/${encodeURIComponent(iccid)}`);
  return sanitizePricing(data);
}

export async function getSimUsage(iccid, { from, to } = {}) {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    const sim = simsMock.find((s) => s.iccid === iccid);
    const series = Array.from({ length: 30 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (29 - i));
      const base = (sim?.mbUsedMtd || 100) / 30;
      const jitter = base * (0.4 + Math.random() * 1.2);
      return {
        date: d.toISOString().slice(0, 10),
        mbUsed: Number(jitter.toFixed(2)),
        sessions: Math.round(3 + Math.random() * 10),
      };
    });
    return {
      from: series[0].date,
      to: series[series.length - 1].date,
      series,
      totals: {
        mbUsed: Number(series.reduce((a, s) => a + s.mbUsed, 0).toFixed(2)),
        sessions: series.reduce((a, s) => a + s.sessions, 0),
      },
      anomalies: [],
    };
  }
  // Lambda translates to:
  //   GET /v1/accounts/{aid}/subscriptions/{sid}/usage-records
  //       ?start-date=YYYY-MM-DD&end-date=YYYY-MM-DD
  // KORE returns per-byte records; Lambda rolls up into daily series so the
  // SPA gets the same { date, mbUsed, sessions } shape regardless of source.
  const qs = (from && to) ? `?from=${from}&to=${to}` : '';
  return liveFetch(`/sims/${encodeURIComponent(iccid)}/usage${qs}`);
}

/**
 * Change SIM lifecycle state — async, returns a request-id.
 *
 * action ∈ { activate, suspend, restore, deactivate, test, modify-plan,
 *            modify-features, modify-cost-center }.
 *
 * KORE pattern: every state change goes through the SAME endpoint
 * (POST /v1/accounts/{aid}/provisioning-requests). The body carries
 * the target state and any feature/plan/cost-center deltas. KORE
 * returns 202 Accepted + a request-id; the actual state change
 * happens 5-60 seconds later (some are near-instant, some take a
 * while when carrier coordination is required).
 *
 * Two ways to learn the outcome:
 *   1. Poll: GET /v1/accounts/{aid}/provisioning-requests/{rid}
 *   2. Webhook: KORE POSTs to our /webhooks/kore endpoint
 *
 * Either path lands in the same DynamoDB row; the SPA gets the
 * eventual status via the WebSocket fan-out from `realtime-stack.ts`.
 *
 * Returns: { requestId, status: 'accepted'|'pending'|'completed'|'failed',
 *            submittedAt, eta? }.
 */
export async function changeSimState(iccid, action, { reason, planId, features, costCenterId } = {}) {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    return {
      requestId: 'mock-pr-' + Math.random().toString(36).slice(2, 12),
      // Legacy alias — old callers reference `transactionId`. Drop after
      // the SIM lifecycle UX is wired to `requestId` throughout.
      transactionId: 'mock-pr-' + Math.random().toString(36).slice(2, 12),
      status: 'accepted',
      submittedAt: new Date().toISOString(),
      eta: new Date(Date.now() + 6000).toISOString(),
    };
  }
  return liveFetch(`/sims/${encodeURIComponent(iccid)}/state`, {
    method: 'POST',
    body: JSON.stringify({ action, reason, planId, features, costCenterId }),
  });
}

/**
 * Poll a provisioning request for its current status.
 * Wraps GET /v1/accounts/{aid}/provisioning-requests/{requestId}.
 *
 * Mock mode advances a deterministic state machine:
 *   accepted → pending (after 1s) → completed (after 4s)
 * So the UI can render the async lifecycle without a real KORE call.
 */
const mockReqState = new Map();
export async function getProvisioningStatus(requestId) {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    let entry = mockReqState.get(requestId);
    if (!entry) {
      entry = { requestId, createdAt: Date.now(), status: 'accepted' };
      mockReqState.set(requestId, entry);
    }
    const age = Date.now() - entry.createdAt;
    entry.status = age < 1000 ? 'accepted'
                 : age < 4000 ? 'pending'
                 : 'completed';
    return {
      requestId,
      status: entry.status,
      submittedAt: new Date(entry.createdAt).toISOString(),
      completedAt: entry.status === 'completed' ? new Date(entry.createdAt + 4000).toISOString() : null,
      error: null,
    };
  }
  return liveFetch(`/sims/provisioning-requests/${encodeURIComponent(requestId)}`);
}

export async function setImeiLock(iccid, imei) {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    return { updated: true };
  }
  // Lambda translates to a feature update on the subscription:
  //   PUT /v1/accounts/{aid}/subscriptions/{sid}/features
  //       (body: { imei_lock: { enabled: true, imei: '<imei>' } })
  // Per KORE docs, imei-lock is a subscription feature, not a separate
  // endpoint. The Lambda may also need to call /provisioning-requests
  // depending on the carrier — confirm at integration time.
  return liveFetch(`/sims/${encodeURIComponent(iccid)}/imei-lock`, {
    method: 'PUT',
    body: JSON.stringify({ imei }),
  });
}

// ─── Lookup endpoints (rate plans / features / cost centers) ─
//
// These rarely change — Lambda should aggressively cache (5-10 min
// TTL) so dashboard loads don't hammer KORE. Used by the Admin
// section to populate plan-switch dropdowns and feature toggles.

export async function listRatePlans() {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    return {
      items: [
        { id: 'plan-pooled-5gb',  name: 'Pooled-5GB',     poolSize: '5 GB',  pricePerSimUsd: null /* sanitized */ },
        { id: 'plan-fixed-1gb',   name: 'Fixed-1GB',      poolSize: null,    pricePerSimUsd: null },
        { id: 'plan-global-2gb',  name: 'Global-2GB',     poolSize: null,    pricePerSimUsd: null },
        { id: 'plan-iot-250mb',   name: 'IoT-250MB',      poolSize: null,    pricePerSimUsd: null },
        { id: 'plan-iot-test',    name: 'IoT-Test',       poolSize: null,    pricePerSimUsd: null },
      ],
    };
  }
  return liveFetch('/sims/plans');
}

export async function listCostCenters() {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    return { items: [
      { id: 'cc-fleet-ops',  name: 'Fleet Operations' },
      { id: 'cc-retail-pos', name: 'Retail POS' },
      { id: 'cc-field-eng',  name: 'Field Engineering' },
    ] };
  }
  return liveFetch('/sims/cost-centers');
}

// ─── Fleets + Network Access Profiles (KORE-side concepts) ─
//
// A Fleet is a KORE-side grouping of SIMs that share a common config
// (rate plan, allowed networks, geofence, IMEI lock policy). A
// NetworkAccessProfile is the carrier-level whitelist/blacklist
// applied to a fleet — which countries, which MNOs, which networks.
//
// These are surfaced in the Admin UI behind the feature flag
// `VITE_FEATURE_SIM_FLEETS` so we can ship the data plumbing now
// without exposing the UI until KORE confirms our account-id has
// Fleet permissions enabled.
//
// Pre-cutover: returns seed fixtures so the data shape is exercised
// end-to-end and Lambdas can be tested against the real ConnectivityPro
// surface without UI changes.

const FLEET_FIXTURES = [
  {
    fleetId: 'flt-truck-mvp',
    name: 'Truck MVP',
    description: 'Mobility tier — Verizon-first with AT&T failover',
    sims: 3,
    networkAccessProfileId: 'nap-us-multicarrier',
    geofence: { countries: ['US', 'CA', 'MX'] },
    imeiLockPolicy: 'soft', // soft = warn, hard = auto-suspend
    rate_plan_id: 'plan-pooled-5gb',
    createdAt: '2025-09-14T15:22:00Z',
  },
  {
    fleetId: 'flt-kiosk-retail',
    name: 'Retail Kiosk',
    description: 'Fixed-location POS terminals — US only',
    sims: 2,
    networkAccessProfileId: 'nap-us-verizon',
    geofence: { countries: ['US'] },
    imeiLockPolicy: 'hard',
    rate_plan_id: 'plan-fixed-1gb',
    createdAt: '2025-10-01T11:00:00Z',
  },
  {
    fleetId: 'flt-eu-coldchain',
    name: 'EU Cold Chain',
    description: 'Refrigerated logistics — pan-European',
    sims: 1,
    networkAccessProfileId: 'nap-eu-roaming',
    geofence: { countries: ['DE', 'FR', 'NL', 'GB', 'IT', 'ES'] },
    imeiLockPolicy: 'soft',
    rate_plan_id: 'plan-global-2gb',
    createdAt: '2025-11-08T08:14:00Z',
  },
];

const NAP_FIXTURES = [
  {
    networkAccessProfileId: 'nap-us-multicarrier',
    name: 'US Multi-Carrier',
    description: 'Verizon + AT&T + T-Mobile, automatic carrier selection',
    countries: ['US'],
    mccmncWhitelist: ['311480', '310410', '310260'],
  },
  {
    networkAccessProfileId: 'nap-us-verizon',
    name: 'US Verizon Only',
    description: 'Locked to Verizon (no failover)',
    countries: ['US'],
    mccmncWhitelist: ['311480'],
  },
  {
    networkAccessProfileId: 'nap-eu-roaming',
    name: 'EU Roaming',
    description: 'All major EU carriers, GSMA roaming enabled',
    countries: ['DE', 'FR', 'NL', 'GB', 'IT', 'ES'],
    mccmncWhitelist: ['*'],
  },
];

export async function listFleets() {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    return { items: FLEET_FIXTURES };
  }
  return liveFetch('/sims/fleets');
}

export async function listNetworkAccessProfiles() {
  if (isMockMode()) {
    await wait(MOCK_LATENCY_MS);
    return { items: NAP_FIXTURES };
  }
  return liveFetch('/sims/network-access-profiles');
}

// ─── Formatting helpers (UI-side) ────────────────────────────

const STATE_LABELS = {
  active: 'Active',
  suspended: 'Suspended',
  test: 'Testing',
  'pre-billing': 'Inventory',
  deactivated: 'Deactivated',
  unknown: 'Unknown',
};

const STATE_TONE = {
  active: 'success',
  suspended: 'warn',
  test: 'info',
  'pre-billing': 'neutral',
  deactivated: 'danger',
  unknown: 'neutral',
};

export function stateLabel(state) { return STATE_LABELS[state] || state; }
export function stateTone(state)  { return STATE_TONE[state]   || 'neutral'; }

export function formatMb(mb) {
  if (mb == null) return '—';
  if (mb >= 1024) return (mb / 1024).toFixed(2) + ' GB';
  return mb.toFixed(1) + ' MB';
}

export function relativeTime(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return Math.round(diff / 60_000) + 'm ago';
  if (diff < 86_400_000) return Math.round(diff / 3_600_000) + 'h ago';
  return Math.round(diff / 86_400_000) + 'd ago';
}
